import { CustomAuthConfiguration, LogLevel } from "@azure/msal-browser/custom-auth";
import type { CustomAuthRequestInterceptor } from "@azure/msal-browser/custom-auth";

export {
  getEmailOtpIdp,
  useGoogleEmailOtpIdp,
  useAcsGoogleEmailOtp,
  useTwilioVerifyGoogleEmailOtp,
  useNumericGoogleEmailOtp,
  useFirebasePasswordlessEmail,
  getGoogleEmailOtpDelivery,
  isSocialSignInEnabled,
  type EmailOtpIdp,
  type GoogleEmailOtpDelivery,
} from "./email-otp-idp";

/** Native Auth public SPA client (must match backend ENTRA_NATIVE_CLIENT_ID / ENTRA_AUDIENCE). */
export const ENTRA_NATIVE_CLIENT_ID = "1c8fb2fc-de56-4467-83dc-32b3aa1ef6cc";

/** External ID (CIAM) tenant. */
export const ENTRA_TENANT_ID = "09b5f110-358e-4126-a1ea-5a711a813cc2";

/** Legacy CIAM hostname (default login URL before custom domain). */
export const ENTRA_TENANT_SUBDOMAIN = "healthauth";

/** Branded Entra External ID login host (Front Door → ciamlogin). */
export const ENTRA_LOGIN_HOST = "login.healthexam.ai";

/**
 * Authority must include the tenant GUID path.
 * Token `iss` may still use `{tenant-guid}.ciamlogin.com`; keep those hosts in knownAuthorities.
 */
export const ENTRA_AUTHORITY = `https://${ENTRA_LOGIN_HOST}/${ENTRA_TENANT_ID}/`;

/** Microsoft Graph audience — never send these tokens to Accounts API. */
export const MICROSOFT_GRAPH_AUDIENCE = "00000003-0000-0000-c000-000000000000";

/** GIS + Graph link after email sign-in (?provider=google). Off until link API is stable. */
export const POST_LOGIN_GOOGLE_LINK_ENABLED = false;

/**
 * resolve-email / Entra federated inference and auto Google redirect on email OTP.
 * Off for now — treat email like any other address (no forced Continue with Google).
 */
export const FEDERATED_EMAIL_OTP_ROUTING_ENABLED = false;

/** Before Entra OTP initiate, POST resolve-email and route by signUpAuthMethod. */
export const ROUTE_EMAIL_BY_SIGN_UP_AUTH_METHOD = true;

/** Scopes for Entra ID token refresh (must include openid + offline_access). */
export const ENTRA_TOKEN_SCOPES = [
  "openid",
  "offline_access",
  "profile",
  "email",
] as const;

/**
 * Native Auth capabilities (email OTP / password in the browser).
 * - mfa_required: honor Entra MFA challenges (e.g. SMS when CA applies).
 * - Do NOT use registration_required here — it prompts Entra “Create account /
 *   register auth method” after Google/Apple federated sign-in. SMS MFA opt-in
 *   uses Accounts API + explicit re-sign-in in the app instead.
 */
export const ENTRA_NATIVE_AUTH_CAPABILITIES = ["mfa_required", "registration_required"] as const;

/**
 * Federated Google/Apple OAuth (loginPopup / loginRedirect).
 * Match Microsoft native-auth sample: empty scopes on the authorize hop avoids
 * extra Graph/consent and native-auth “Create account” registration UI when possible.
 * ID token + refresh are still issued for the SPA client; email OTP uses ENTRA_TOKEN_SCOPES.
 */
export const ENTRA_SOCIAL_OAUTH_SCOPES: readonly string[] = [];

/** Local ng serve origin (SSL required for Firebase Google OAuth). */
export const LOCAL_DEV_ORIGIN = "https://localhost:4200";

/**
 * SPA redirect URI for MSAL v5 loginPopup (redirect bridge page).
 * Must be registered as SPA type in Entra (not Web).
 */
export const ENTRA_REDIRECT_URI =
  typeof window !== "undefined"
    ? `${window.location.origin}/redirect`
    : `${LOCAL_DEV_ORIGIN}/redirect`;

export const ENTRA_POST_LOGOUT_REDIRECT_URI =
  typeof window !== "undefined" ? window.location.origin : LOCAL_DEV_ORIGIN;

/**
 * Native auth API proxy endpoint.
 * - Local dev: use explicit local CORS proxy on :3001
 * - Deployed environments: use same-origin /api so the server can proxy to Entra
 *   (e.g. https://dev-auth.healthexam.ai/api/oauth2/v2.0/initiate)
 */
const resolveAuthApiProxyUrl = (): string => {
  if (typeof window === "undefined") {
    return "http://localhost:3001/api";
  }

  const host = window.location.hostname.toLowerCase();
  const isLocalHost = host === "localhost" || host === "127.0.0.1";
  if (isLocalHost) {
    return "http://localhost:3001/api";
  }

  return `${window.location.origin}/api`;
};

const requestInterceptor: CustomAuthRequestInterceptor = {
  addAdditionalHeaderFields(requestUrl: URL) {
    if (requestUrl.pathname.endsWith("/oauth2/v2.0/initiate")) {
      return {
        "X-health-exam-client": "wa-account-v2",
      };
    }
    return null;
  },
};

export const customAuthConfig: CustomAuthConfiguration = {
  customAuth: {
    challengeTypes: ["password", "oob"],
    // Handle Entra SMS MFA when Conditional Access targets the user (opt-in group).
    // CA policy controls who must use SMS — not this flag.
    capabilities: [...ENTRA_NATIVE_AUTH_CAPABILITIES],
    // CORS proxy for Native Auth email OTP / password only (not social popup).
    authApiProxyUrl: resolveAuthApiProxyUrl(),
    requestInterceptor: requestInterceptor,
  },
  auth: {
    clientId: ENTRA_NATIVE_CLIENT_ID,
    authority: ENTRA_AUTHORITY,
    knownAuthorities: [
      ENTRA_LOGIN_HOST,
      `${ENTRA_TENANT_SUBDOMAIN}.ciamlogin.com`,
      `${ENTRA_TENANT_ID}.ciamlogin.com`,
    ],
    redirectUri: ENTRA_REDIRECT_URI,
    postLogoutRedirectUri: ENTRA_POST_LOGOUT_REDIRECT_URI,
  },
  cache: {
    // localStorage keeps MSAL refresh tokens across browser restarts for silent renew.
    cacheLocation: "localStorage",
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};
